<template>
  <dx-modal
    id="modal-schedule-diagnostic"
    title="Por que não aparece?"
    :value="show"
    @input="close"
    size="lg"
  >
    <div class="diag-init" v-if="initializing">
      <div class="loading loading-lg"/>
    </div>

    <template v-else>
    <ul class="step">
      <li
        v-for="item in steps"
        class="step-item"
        :class="{ active: step === item.id, visited: item.id < step }"
        :key="item.id"
      ><a href="#" @click.prevent="goToStep(item.id)">{{ item.title }}</a></li>
    </ul>

    <div class="diag-head">
      <span class="diag-eyebrow">Passo {{ step }} de {{ steps.length }}</span>
      <h5 class="diag-title">{{ currentStep.heading }}</h5>
      <p class="diag-hint">{{ currentStep.hint }}</p>
    </div>

    <div class="diag-form">
      <div class="diag-locked" v-if="step > 1">
        <div class="diag-locked-row">
          <span class="diag-locked-label">Agenda</span>
          <span class="diag-locked-value">{{ scheduleName }}</span>
        </div>
        <div class="diag-locked-row" v-if="step > 2">
          <span class="diag-locked-label">Horários</span>
          <span class="diag-locked-value">{{ selectedHoursLabel }}</span>
        </div>
      </div>

      <div class="diag-group" v-if="step === 1">
        <div
          class="form-group"
          :class="{ 'has-error': $v.form.scheduleId.$error }"
        >
          <label class="form-label" for="diagnostic-schedule">Agenda</label>
          <select
            id="diagnostic-schedule"
            class="form-select"
            v-model="form.scheduleId"
            @change="$v.form.scheduleId.$touch()"
          >
            <option value="">[Selecione a agenda]</option>
            <option v-for="item in schedules" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </select>
          <template v-if="$v.form.scheduleId.$error">
            <div class="form-input-hint" v-if="!$v.form.scheduleId.required">
              Campo obrigatório
            </div>
          </template>
        </div>
      </div>

      <div class="diag-group" v-if="step === 2">
        <div class="diag-group-head">
          <strong>Horários da agenda</strong>
          <a
            href="#"
            class="diag-select-all"
            v-if="hours.length"
            @click.prevent="toggleAll"
          >{{ allSelected ? 'Desmarcar todos' : 'Selecionar todos' }}</a>
        </div>
        <div class="loading loading-lg diag-hours-loading" v-if="loadingHours"/>
        <div class="diag-empty" v-else-if="hours.length === 0">
          Esta agenda não tem horários semanais nem escalas futuras.
        </div>
        <ul class="diag-hours" v-else>
          <li
            class="diag-hour"
            v-for="item in hours"
            :key="item.id"
            :class="{ 'is-selected': form.configIds.includes(item.id) }"
          >
            <label class="form-checkbox">
              <input type="checkbox" :value="item.id" v-model="form.configIds">
              <i class="form-icon"/>
              <span class="diag-hour-day">{{ item.dayLabel }}</span>
              <span class="diag-hour-type">{{ item.type === 'week' ? 'Semanal' : 'Escala' }}</span>
              <span class="diag-hour-ranges">{{ item.rangesLabel }}</span>
              <span class="diag-hour-meta">
                {{ item.insurances }} convênios · {{ item.expenses }} serviços
              </span>
            </label>
          </li>
        </ul>
      </div>

      <div class="diag-group" v-if="step === 3">
        <div class="columns">
          <div class="column col-6 col-sm-12 form-group">
            <label class="form-label" for="diagnostic-plan">Convênio</label>
            <select
              id="diagnostic-plan"
              class="form-select"
              v-model="form.planId"
              :disabled="loadingPlans"
            >
              <option value="">{{ loadingPlans ? 'Carregando...' : '[Não informar]' }}</option>
              <option v-for="item in plans" :key="item.plan.id" :value="item.plan.id">
                {{ item.customName }}
              </option>
            </select>
          </div>
          <div class="column col-6 col-sm-12 form-group">
            <label class="form-label" for="diagnostic-expense">Serviço</label>
            <dx-autocomplete
              v-model="expense"
              :source="findExpense"
              label="name"
              track-by="id"
              :debounce="600"
              :readonly="!!form.expenseId"
              @select="selectExpense"
              placeholder="Buscar pelo nome ou código"
              input-id="diagnostic-expense"
            >
              <template v-slot:action>
                <button
                  class="btn btn-action input-group-btn btn-icon"
                  :class="form.expenseId ? 'btn-gray' : 'btn-neutral'"
                  tabindex="-1"
                  @click="form.expenseId ? clearExpense() : (findExpenseModalShow = true)"
                >
                  <fa-icon :icon="['fal', form.expenseId ? 'times' : 'search']"/>
                </button>
              </template>
              <template v-slot="{ item }">
                <a>{{ item.name }}</a>
              </template>
            </dx-autocomplete>
          </div>
        </div>
      </div>

      <div class="diag-form-foot">
        <button
          class="btn btn-gray-outline"
          v-if="step > 1"
          :disabled="loading || !!fixing"
          @click="goToStep(step - 1)"
        >Voltar</button>
        <span v-else/>
        <button
          v-if="step < steps.length"
          class="btn btn-primary btn-icon btn-icon-right"
          :disabled="!canAdvance"
          @click="goToStep(step + 1)"
        >Avançar<fa-icon :icon="['fal', 'chevron-right']"/></button>
        <button
          v-else
          class="btn btn-primary btn-icon btn-icon-left"
          :class="{ loading }"
          :disabled="loading || !!fixing || !canRun"
          @click="run"
        >
          <fa-icon :icon="['fal', 'search']"/>
          {{ result ? 'Verificar novamente' : 'Verificar' }}
        </button>
      </div>
    </div>

    <div class="diag-result" :class="{ 'is-loading': loading }" v-if="result && step === 3">
      <div class="diag-summary" :class="`is-${summary.type}`">
        <span class="diag-summary-icon">
          <fa-icon :icon="['fal', summary.icon]"/>
        </span>
        <div class="diag-summary-text">
          <strong>{{ summary.title }}</strong>
          <span>{{ summary.text }}</span>
        </div>
      </div>

      <div class="diag-permission" v-if="missingPermissions.length">
        <span class="diag-permission-icon">
          <fa-icon :icon="['fal', 'user-lock']"/>
        </span>
        <div class="diag-permission-text">
          <strong>Você pode consultar, mas não corrigir por esta tela</strong>
          <span>
            Para aplicar as correções por essa tela é preciso ter permissão para editar
            {{ canUpdateSchedule ? 'tabelas de preço' : 'configurações de agenda' }}.
            Peça a liberação ao administrador do sistema.
          </span>
          <div class="diag-permission-list">
            <span class="diag-permission-label">Permissões necessárias:</span>
            <span
              class="diag-permission-name"
              v-for="name in missingPermissions"
              :key="name"
            >{{ name }}</span>
          </div>
        </div>
      </div>

      <template v-if="pendingRows.length">
        <div class="diag-section-head">O que corrigir</div>
        <ul class="diag-list">
          <li
            class="diag-row"
            v-for="item in pendingRows"
            :key="item.key"
            :class="`is-${item.status}`"
          >
            <span class="diag-row-icon">
              <fa-icon :icon="['fal', statusIcon(item.status)]"/>
            </span>
            <div class="diag-row-text">
              <div class="diag-row-title">
                {{ item.title }}
                <span class="status-pill" :class="`is-${item.status}`">
                  {{ item.status === 'error' ? 'Impede' : 'Atenção' }}
                </span>
              </div>
              <div class="diag-row-message">{{ item.message }}</div>
            </div>
            <div class="diag-row-action" v-if="canApplyFix(item.fix)">
              <button
                class="btn btn-sm btn-icon btn-icon-left"
                :class="[
                  item.fix.type === 'link' ? 'btn-gray-outline' : 'btn-primary',
                  { loading: fixing === item.key },
                ]"
                :disabled="loading || !!fixing"
                @click="applyFix(item)"
              >
                <fa-icon :icon="['fal', item.fix.type === 'link' ? 'external-link' : 'plus']"/>
                {{ item.fix.label }}
              </button>
            </div>
          </li>
        </ul>
      </template>

      <template v-if="result.plan || result.expense">
        <div class="diag-section-head">Onde está e onde não está</div>
        <div class="diag-matrix-wrapper">
          <table class="diag-matrix">
            <thead>
              <tr>
                <th>Horário</th>
                <th v-if="result.plan">{{ result.plan.name }}</th>
                <th v-if="result.expense">{{ result.expense.name }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="hour in result.hours" :key="hour.id">
                <td>
                  <div class="diag-matrix-day">{{ hour.label }} · {{ hour.ranges }}</div>
                  <div
                    class="diag-matrix-validity"
                    :class="{ 'text-error': !hour.validity.valid }"
                  >{{ hour.validity.label }}</div>
                </td>
                <td v-if="result.plan">
                  <span class="status-pill is-ok" v-if="hour.hasPlan">Está</span>
                  <template v-else>
                    <span class="status-pill is-error">Não está</span>
                    <button
                      v-if="canUpdateSchedule"
                      class="btn btn-sm btn-link"
                      :class="{ loading: fixing === `plan-${hour.id}` }"
                      :disabled="loading || !!fixing"
                      @click="addToHour(hour, 'plan')"
                    >Adicionar</button>
                  </template>
                </td>
                <td v-if="result.expense">
                  <span class="status-pill is-ok" v-if="hour.hasExpense">Está</span>
                  <span
                    class="status-pill is-muted"
                    v-else-if="hour.emptyExpenses"
                    data-tooltip="Horário sem lista de serviços: aceita todos"
                  >Sem lista</span>
                  <template v-else>
                    <span class="status-pill is-error">Não está</span>
                    <button
                      v-if="canUpdateSchedule"
                      class="btn btn-sm btn-link"
                      :class="{ loading: fixing === `expense-${hour.id}` }"
                      :disabled="loading || !!fixing"
                      @click="addToHour(hour, 'expense')"
                    >Adicionar</button>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <template v-if="checkedRows.length">
        <a href="#" class="diag-toggle" @click.prevent="showChecked = !showChecked">
          <span>
            {{ showChecked ? 'Ocultar' : 'Mostrar' }} itens verificados ({{ checkedRows.length }})
          </span>
          <fa-icon :icon="['fal', showChecked ? 'chevron-up' : 'chevron-down']"/>
        </a>
        <ul class="diag-list is-checked" v-if="showChecked">
          <li
            class="diag-row"
            v-for="item in checkedRows"
            :key="item.key"
            :class="`is-${item.status}`"
          >
            <span class="diag-row-icon">
              <fa-icon :icon="['fal', statusIcon(item.status)]"/>
            </span>
            <div class="diag-row-text">
              <div class="diag-row-title">{{ item.title }}</div>
              <div class="diag-row-message">{{ item.message }}</div>
            </div>
          </li>
        </ul>
      </template>
    </div>

    </template>

    <find-expense-modal
      v-if="findExpenseModalShow"
      @selected="findExpenseSelected"
      @close="findExpenseModalShow = false"
    />

    <template v-slot:footer>
      <button class="btn" @click="close">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import { UPDATE_SCHEDULE } from '@/data/actions/modules/clinical';
import { UPDATE_EXPENSE_TABLE } from '@/data/actions/modules/general';
import FindExpenseModal from '@/app/expense/components/modals/FindExpenseModal.vue';

export default {
  components: {
    FindExpenseModal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    params: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      initializing: true,
      step: 1,
      steps: [
        {
          id: 1,
          title: 'Agenda',
          heading: 'Qual agenda você quer verificar?',
          hint: 'Escolha a agenda onde o convênio ou o serviço não apareceu.',
        },
        {
          id: 2,
          title: 'Horários',
          heading: 'Quais horários?',
          hint: 'Marque os horários da agenda que devem ser verificados.',
        },
        {
          id: 3,
          title: 'Convênio e serviço',
          heading: 'O que não apareceu?',
          hint: 'Informe o convênio, o serviço ou os dois. A verificação mostra onde está, '
            + 'onde não está e o que corrigir.',
        },
      ],
      loading: false,
      loadingHours: false,
      loadingPlans: false,
      hoursScheduleId: '',
      fixing: null,
      result: null,
      checkedKey: '',
      showChecked: false,
      findExpenseModalShow: false,
      expense: null,
      hours: [],
      plans: [],
      form: {
        scheduleId: '',
        configIds: [],
        planId: '',
        expenseId: '',
        expenseName: '',
      },
    };
  },
  validations() {
    return {
      form: {
        scheduleId: { required: value => !!value },
        configIds: { required: value => value.length > 0 },
      },
    };
  },
  computed: {
    ...mapState({
      filter: ({ appointment }) => appointment.filter,
    }),
    schedules() {
      return this.filter.schedules || [];
    },
    canUpdateSchedule() {
      return this.$can(UPDATE_SCHEDULE);
    },
    missingPermissions() {
      if (!this.result) return [];

      const fixes = this.pendingRows.filter(({ fix }) => fix).map(({ fix }) => fix);
      const missingInHours = (this.result.hours || []).some(hour => (
        (this.result.plan && !hour.hasPlan)
        || (this.result.expense && !hour.hasExpense && !hour.emptyExpenses)
      ));

      const names = [];
      if (!this.canUpdateSchedule && (fixes.length || missingInHours)) {
        names.push('Gerenciamento de agendas: Atualizar');
      }
      if (
        fixes.some(({ type }) => type === 'add-to-table')
        && !this.$can(UPDATE_EXPENSE_TABLE)
      ) {
        names.push('Tabelas de Preços: Atualizar');
      }
      return names;
    },
    selectedPlan() {
      return this.plans.find(({ plan }) => plan.id === this.form.planId) || null;
    },
    currentStep() {
      return this.steps.find(({ id }) => id === this.step);
    },
    canAdvance() {
      if (this.step === 1) return !!this.form.scheduleId;
      if (this.step === 2) return !this.loadingHours && this.form.configIds.length > 0;
      return false;
    },
    scheduleName() {
      const found = this.schedules.find(({ id }) => id === this.form.scheduleId);
      return found ? found.name : '';
    },
    selectedHoursLabel() {
      const selected = this.hours.filter(({ id }) => this.form.configIds.includes(id));
      const names = selected.slice(0, 4).map(({ dayLabel }) => dayLabel).join(', ');
      const more = selected.length > 4 ? ` e mais ${selected.length - 4}` : '';
      return `${selected.length} ${selected.length === 1 ? 'horário' : 'horários'}: ${names}${more}`;
    },
    canRun() {
      return !!this.form.scheduleId
        && this.form.configIds.length > 0
        && !!(this.form.planId || this.form.expenseId);
    },
    allSelected() {
      return this.hours.length > 0 && this.form.configIds.length === this.hours.length;
    },
    formKey() {
      return JSON.stringify([
        this.form.scheduleId,
        [...this.form.configIds].sort(),
        this.form.planId,
        this.form.expenseId,
      ]);
    },
    pendingRows() {
      return (this.result ? this.result.items : [])
        .filter(({ status }) => ['error', 'warning'].includes(status));
    },
    checkedRows() {
      return (this.result ? this.result.items : [])
        .filter(({ status }) => !['error', 'warning'].includes(status));
    },
    summary() {
      const total = this.pendingRows.length;
      if (total === 0) {
        return {
          type: 'ok',
          icon: 'check-circle',
          title: 'Nenhuma pendência encontrada',
          text: 'Tudo certo nos horários selecionados.',
        };
      }
      return {
        type: 'pending',
        icon: 'exclamation-triangle',
        title: total === 1 ? '1 pendência encontrada' : `${total} pendências encontradas`,
        text: 'Corrija os itens abaixo para que apareçam na agenda.',
      };
    },
  },
  watch: {
    formKey(value) {
      if (this.result && this.checkedKey !== value) {
        this.result = null;
      }
    },
    'form.scheduleId': function watchScheduleId(value, oldValue) {
      if (!this.initializing && value !== oldValue) {
        this.result = null;
        this.hours = [];
        this.form.configIds = [];
        this.hoursScheduleId = '';
      }
    },
  },
  async mounted() {
    this.form.planId = this.params.planId || '';
    this.expense = this.params.search ? { name: this.params.search } : null;
    this.form.scheduleId = this.params.scheduleId && this.params.scheduleId !== 'ALL'
      ? this.params.scheduleId
      : '';

    const jumpToSearch = !!(this.form.planId || this.params.expenseId || this.params.search);

    if (!this.form.scheduleId || !jumpToSearch) {
      this.initializing = false;
      return;
    }

    await Promise.all([
      this.loadHours(),
      this.loadPlans(),
      this.loadExpense(this.params.expenseId),
    ]);

    if (this.form.configIds.length) {
      this.step = 3;
    }

    if (this.params.date && this.canRun) {
      await this.run();
    }

    this.initializing = false;
  },
  methods: {
    ...mapActions({
      loadCalendar: 'loadCalendar',
    }),
    close() {
      this.$emit('close');
    },
    async loadHours() {
      this.hours = [];
      this.form.configIds = [];
      if (!this.form.scheduleId) return;

      this.hoursScheduleId = this.form.scheduleId;

      this.loadingHours = true;
      try {
        const { data } = await this.$httpX.get(`/schedules/${this.form.scheduleId}`);
        const today = moment().format('YYYY-MM-DD');
        this.hours = (data.schedules || [])
          .filter(({ type, day }) => type === 'week' || day >= today)
          .map(item => ({
            ...item,
            dayLabel: item.type === 'week'
              ? item.day.charAt(0).toUpperCase() + item.day.slice(1)
              : moment(item.day).format('DD/MM/YYYY · ddd'),
            rangesLabel: (item.ranges || []).length
              ? [...item.ranges]
                .sort((a, b) => a.start.localeCompare(b.start))
                .map(({ start, end }) => `${start}–${end}`)
                .join(', ')
              : 'Encaixe',
          }));
        this.form.configIds = this.initialSelection();
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.loadingHours = false;
      }
    },
    initialSelection() {
      if (!this.params.date) return this.hours.map(({ id }) => id);
      const date = moment(this.params.date);
      const weekDay = date.format('dddd');
      const selected = this.hours
        .filter(({ type, day }) => (
          (type === 'week' && day === weekDay) || (type === 'scale' && day === this.params.date)
        ))
        .map(({ id }) => id);
      return selected.length ? selected : this.hours.map(({ id }) => id);
    },
    goToStep(id) {
      if (id < 1 || id > this.steps.length || id === this.step) return;
      if (id > this.step && (id !== this.step + 1 || !this.canAdvance)) return;
      if (id === 1) this.result = null;
      this.step = id;
      if (id === 2 && this.hoursScheduleId !== this.form.scheduleId) {
        this.loadHours();
      }
      if (id === 3 && this.plans.length === 0 && !this.loadingPlans) {
        this.loadPlans();
      }
    },
    toggleAll() {
      this.form.configIds = this.allSelected ? [] : this.hours.map(({ id }) => id);
    },
    async loadPlans() {
      this.loadingPlans = true;
      try {
        const { data } = await this.$httpX
          .get('/insurance-plans', { params: { active: true, limit: 0 } });
        this.plans = data.items;
        if (!this.form.planId && this.params.insuranceId) {
          const found = this.plans.filter(({ id }) => id === this.params.insuranceId);
          if (found.length === 1) this.form.planId = found[0].plan.id;
        }
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.loadingPlans = false;
      }
    },
    async loadExpense(id) {
      if (!id) return;
      try {
        const { data } = await this.$httpX.get(`/expenses/${id}`);
        this.selectExpense(data);
      } catch (e) {
        this.clearExpense();
      }
    },
    findExpense(search) {
      return this.$httpX
        .get('/expenses', { params: { search, active: true, limit: 20 } })
        .then(({ data }) => data.items);
    },
    selectExpense(item) {
      if (!item || !item.id) return;
      this.form.expenseId = item.id;
      this.form.expenseName = item.name;
      this.expense = { id: item.id, name: item.name };
    },
    findExpenseSelected(items) {
      this.findExpenseModalShow = false;
      if (items && items.length > 0) {
        this.selectExpense(items[0]);
      }
    },
    clearExpense() {
      this.form.expenseId = '';
      this.form.expenseName = '';
      this.expense = null;
    },
    async run() {
      this.$v.form.$touch();
      if (!this.canRun) return;
      if (this.$v.form.$error) return;

      this.loading = true;
      const key = this.formKey;
      try {
        const { data } = await this.$httpX.get('/schedule-diagnostics', {
          params: {
            scheduleId: this.form.scheduleId,
            configIds: this.form.configIds.join(','),
            planId: this.form.planId || undefined,
            insuranceId: this.selectedPlan ? this.selectedPlan.id : undefined,
            expenseId: this.form.expenseId || undefined,
          },
        });
        if (key !== this.formKey) return;
        this.result = data;
        this.checkedKey = key;
        this.showChecked = this.pendingRows.length === 0;
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.loading = false;
      }
    },
    addToHour(hour, target) {
      const fix = target === 'plan'
        ? { type: 'bulk-add', ids: [hour.id], insurancePlans: [{ id: this.result.plan.id }] }
        : { type: 'bulk-add', ids: [hour.id], expenses: [this.result.expense.id] };
      return this.applyFix({ key: `${target}-${hour.id}`, fix });
    },
    canApplyFix(fix) {
      if (!fix || !this.canUpdateSchedule) return false;
      return fix.type !== 'add-to-table' || this.$can(UPDATE_EXPENSE_TABLE);
    },
    async applyFix(item) {
      const { fix } = item;

      if (!this.canApplyFix(fix)) return;

      if (fix.type === 'link') {
        this.close();
        this.$router.push(fix.url);
        return;
      }

      this.fixing = item.key;
      try {
        if (fix.type === 'add-to-table') {
          await this.$httpX.post('/expense-tables/expenses', {
            items: [{ tableId: fix.tableId, expenseId: fix.expenseId }],
          });
          this.$toast.show('Serviço incluído na tabela de preço');
        } else if (fix.type === 'bulk-add') {
          await this.$httpX.post('/schedule-config/bulk-items', {
            action: 'add',
            ids: fix.ids,
            insurancePlans: fix.insurancePlans || [],
            expenses: fix.expenses || [],
            professionals: [],
          });
          this.$toast.show('Adicionado aos horários');
          this.loadCalendar();
        }
        await this.run();
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.fixing = null;
      }
    },
    statusIcon(status) {
      return {
        ok: 'check-circle',
        warning: 'exclamation-triangle',
        error: 'times-circle',
        info: 'info-circle',
      }[status] || 'info-circle';
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables';

#modal-schedule-diagnostic {
  .step {
    border-bottom: $border-width solid $border-color;
    counter-reset: diag-step;
    margin-bottom: $layout-spacing-lg;
    padding-bottom: $layout-spacing-lg;
    .step-item {
      &:not(:first-child)::before {
        background: lighten($primary-color, 34%);
        height: 1px;
        top: .45rem;
      }
      a {
        color: $gray-color-dark;
        cursor: default;
        font-size: $font-size-sm;
        font-weight: 500;
        padding-top: 1.35rem;
        text-decoration: none;
        &:focus {
          box-shadow: none;
          outline: 0;
        }
        &::before {
          align-items: center;
          background: $light-color;
          border: 1px solid $border-color-dark;
          border-radius: 50%;
          color: $gray-color-dark;
          content: counter(diag-step);
          counter-increment: diag-step;
          display: flex;
          font-size: .5rem;
          font-weight: 600;
          height: .9rem;
          justify-content: center;
          line-height: 1;
          top: 0;
          width: .9rem;
        }
      }
      &.visited a {
        cursor: pointer;
        &::before {
          background: lighten($primary-color, 33%);
          border-color: lighten($primary-color, 30%);
          color: $primary-color;
          content: '\2713';
        }
        &:hover {
          color: $primary-color;
        }
      }
      &.active a {
        color: $secondary-color-dark;
        font-weight: 600;
        &::before {
          border-color: $primary-color;
          color: $primary-color;
        }
      }
      &.active ~ .step-item {
        &::before {
          background: $border-color;
        }
        a {
          color: $gray-color;
          &::before {
            border-color: $border-color-dark;
            color: $gray-color;
          }
        }
      }
    }
  }
  .diag-locked {
    background-color: $gray-color-ultra-light;
    border-bottom: $border-width solid $border-color;
    padding: $layout-spacing-sm $layout-spacing-lg;
  }
  .diag-locked-row {
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing;
    padding: .1rem 0;
  }
  .diag-locked-label {
    color: $gray-color-dark;
    flex-shrink: 0;
    width: 4.5rem;
  }
  .diag-locked-value {
    color: $gray-color-900;
    font-weight: 600;
    min-width: 0;
  }
  .diag-init {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 12rem;
  }
  .diag-head {
    margin-bottom: $layout-spacing-lg;
  }
  .diag-eyebrow {
    color: $primary-color;
    display: block;
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
  }
  .diag-title {
    color: $gray-color-900;
    font-size: .9rem;
    font-weight: 700;
    margin: $layout-spacing-sm 0 0;
  }
  .diag-hint {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin: $layout-spacing-sm 0 0;
    max-width: 40rem;
  }

  .diag-form {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
  }
  .diag-group {
    padding: $layout-spacing $layout-spacing-lg;
    & + .diag-group {
      border-top: $border-width solid $border-color;
    }
    .form-group {
      margin-bottom: 0;
    }
  }
  .diag-group-head {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: 0 $layout-spacing;
    margin-bottom: $layout-spacing-sm;
    strong {
      color: $gray-color-900;
      font-size: $font-size-sm;
      font-weight: 700;
    }
    small {
      color: $gray-color-dark;
      font-size: $font-size-xs;
    }
  }
  .diag-select-all {
    font-size: $font-size-xs;
    margin-left: auto;
  }
  .diag-hours-loading {
    min-height: 2rem;
  }
  .diag-empty {
    color: $gray-color-dark;
    font-size: $font-size-sm;
  }
  .diag-hours {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    list-style: none;
    margin: 0;
    max-height: 12rem;
    overflow-y: auto;
    padding: 0;
  }
  .diag-hour {
    margin: 0;
    & + .diag-hour {
      border-top: $border-width solid $border-color;
    }
    &:hover {
      background-color: $gray-color-ultra-light;
    }
    &.is-selected {
      background-color: lighten($primary-color, 34%);
    }
    .form-checkbox {
      align-items: center;
      display: grid;
      gap: $layout-spacing;
      grid-template-columns: 7rem 4rem minmax(0, 1fr) auto;
      margin: 0;
      padding: $layout-spacing-sm $layout-spacing $layout-spacing-sm 1.8rem;
      position: relative;
      .form-icon {
        left: $layout-spacing;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }
  .diag-hour-day {
    color: $gray-color-900;
    font-weight: 600;
  }
  .diag-hour-type {
    color: $gray-color-dark;
    font-size: $font-size-xs;
  }
  .diag-hour-ranges {
    background-color: #FFF8E6;
    border: $border-width solid #F5DD8E;
    border-radius: 1rem;
    color: $gray-color-900;
    font-size: $font-size-xs;
    justify-self: start;
    padding: .05rem .5rem;
  }
  .diag-hour-meta {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    white-space: nowrap;
  }

  .diag-form-foot {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border-top: $border-width solid $border-color;
    display: flex;
    gap: $layout-spacing;
    justify-content: space-between;
    padding: $layout-spacing $layout-spacing-lg;
    .btn {
      flex-shrink: 0;
    }
  }

  .diag-result {
    margin-top: $layout-spacing-lg;
    transition: opacity .15s ease;
    &.is-loading {
      opacity: .55;
      pointer-events: none;
    }
  }
  .diag-summary {
    align-items: flex-start;
    border: $border-width solid;
    border-left-width: 4px;
    border-radius: $border-radius;
    display: flex;
    gap: $layout-spacing;
    padding: $layout-spacing $layout-spacing-lg;
    &.is-pending {
      background-color: #FFF8E6;
      border-color: #F2C94C;
      .diag-summary-icon {
        color: #D9A21B;
      }
      strong {
        color: $gray-color-900;
      }
    }
    &.is-ok {
      background-color: #F2FAF5;
      border-color: #BFE3CD;
      border-left-color: #2FA36B;
      .diag-summary-icon {
        color: #2FA36B;
      }
      strong {
        color: #1F7A4E;
      }
    }
  }
  .diag-summary-icon {
    flex-shrink: 0;
    font-size: 1rem;
    line-height: 1.2;
  }
  .diag-summary-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    strong {
      font-size: $font-size-lg;
      font-weight: 700;
    }
    span {
      color: $gray-color-dark;
      font-size: $font-size-sm;
    }
  }

  .diag-permission {
    align-items: flex-start;
    background-color: #FDF1F0;
    border: $border-width solid #F3C7C3;
    border-left: 4px solid $error-color;
    border-radius: $border-radius;
    display: flex;
    gap: $layout-spacing;
    margin-top: $layout-spacing;
    padding: $layout-spacing $layout-spacing-lg;
  }
  .diag-permission-icon {
    color: $error-color;
    flex-shrink: 0;
    font-size: 1rem;
    line-height: 1.2;
  }
  .diag-permission-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    strong {
      color: $gray-color-900;
      font-size: $font-size-lg;
      font-weight: 700;
    }
    > span {
      color: $gray-color-dark;
      font-size: $font-size-sm;
    }
  }
  .diag-permission-list {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
    margin-top: $layout-spacing-sm;
  }
  .diag-permission-label {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-weight: 600;
  }
  .diag-permission-name {
    background-color: $light-color;
    border: $border-width solid #F3C7C3;
    border-radius: 1rem;
    color: $error-color;
    font-size: $font-size-xs;
    font-weight: 600;
    padding: .1rem .5rem;
  }

  .diag-section-head {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-weight: 700;
    letter-spacing: .04em;
    margin: $layout-spacing-lg 0 $layout-spacing-sm;
    text-transform: uppercase;
  }
  .diag-list {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    list-style: none;
    margin: 0;
    overflow: hidden;
    padding: 0;
  }
  .diag-row {
    align-items: center;
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: 1rem minmax(0, 1fr) auto;
    margin: 0;
    padding: $layout-spacing $layout-spacing-lg;
    & + .diag-row {
      border-top: $border-width solid $border-color;
    }
    &:hover {
      background-color: $gray-color-ultra-light;
    }
    &.is-error {
      box-shadow: inset 3px 0 0 $error-color;
      .diag-row-icon {
        color: $error-color;
      }
    }
    &.is-warning {
      box-shadow: inset 3px 0 0 #F2C94C;
      .diag-row-icon {
        color: #D9A21B;
      }
    }
    &.is-ok .diag-row-icon {
      color: #2FA36B;
    }
    &.is-info .diag-row-icon {
      color: $info-color;
    }
  }
  .diag-row-icon {
    align-self: flex-start;
    font-size: $font-size-lg;
    line-height: 1.2;
  }
  .diag-row-text {
    min-width: 0;
  }
  .diag-row-title {
    align-items: center;
    color: $gray-color-900;
    display: flex;
    flex-wrap: wrap;
    font-weight: 600;
    gap: $layout-spacing-sm;
  }
  .diag-row-message {
    color: $gray-color-dark;
    font-size: $font-size-sm;
  }
  .diag-row-action {
    white-space: nowrap;
  }
  .diag-list.is-checked {
    .diag-row {
      padding-bottom: $layout-spacing-sm;
      padding-top: $layout-spacing-sm;
    }
    .diag-row-title {
      font-size: $font-size-sm;
    }
    .diag-row-message {
      font-size: $font-size-xs;
    }
  }

  .diag-matrix-wrapper {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    overflow-x: auto;
  }
  .diag-matrix {
    border-collapse: collapse;
    width: 100%;
    th {
      background-color: $gray-color-ultra-light;
      border-bottom: $border-width solid $border-color;
      color: $gray-color-dark;
      font-size: $font-size-xs;
      font-weight: 700;
      padding: $layout-spacing-sm $layout-spacing;
      text-align: left;
    }
    td {
      padding: $layout-spacing-sm $layout-spacing;
      vertical-align: middle;
      white-space: nowrap;
    }
    tr + tr td {
      border-top: $border-width solid $border-color;
    }
    tbody tr:hover td {
      background-color: $gray-color-ultra-light;
    }
    .btn-link {
      margin-left: $layout-spacing-sm;
    }
  }
  .diag-matrix-day {
    color: $gray-color-900;
    font-weight: 600;
  }
  .diag-matrix-validity {
    color: $gray-color-dark;
    font-size: $font-size-xs;
  }

  .status-pill {
    background-color: $light-color;
    border: $border-width solid;
    border-radius: 1rem;
    display: inline-block;
    font-size: $font-size-xs;
    font-weight: 600;
    line-height: 1;
    padding: .15rem .45rem;
    white-space: nowrap;
    &.is-error {
      border-color: $error-color;
      color: $error-color;
    }
    &.is-warning {
      border-color: #F2C94C;
      color: #8A5A00;
    }
    &.is-ok {
      border-color: #BFE3CD;
      color: #1F7A4E;
    }
    &.is-muted {
      border-color: $border-color-dark;
      color: $gray-color-dark;
    }
  }

  .diag-toggle {
    align-items: center;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    color: $gray-color-dark;
    display: flex;
    font-size: $font-size-sm;
    justify-content: space-between;
    margin-top: $layout-spacing-lg;
    padding: $layout-spacing-sm $layout-spacing-lg;
    text-decoration: none;
    &:hover {
      background-color: $gray-color-ultra-light;
      color: $primary-color;
    }
    & + .diag-list {
      border-top: 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  @media screen and (max-width: $size-sm) {
    .diag-group,
    .diag-form-foot,
    .diag-summary,
    .diag-permission,
    .diag-row {
      padding-left: $layout-spacing;
      padding-right: $layout-spacing;
    }
    .diag-hour .form-checkbox {
      grid-template-columns: minmax(0, 1fr) auto;
    }
    .diag-hour-meta {
      display: none;
    }
    .diag-form-foot {
      align-items: stretch;
      flex-direction: column-reverse;
      .btn {
        width: 100%;
      }
    }
    .diag-row-action {
      grid-column: 2 / 4;
      white-space: normal;
    }
  }
}
</style>
